import React from 'react';
import { motion } from 'framer-motion';
import { UtensilsCrossed, Users, Shield, Sparkles } from 'lucide-react';
import { useApp } from '../context/AppContext';

const WelcomeScreen: React.FC = () => {
  const { setCurrentScreen } = useApp();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A0A0B] via-[#1C1C1E] to-[#0A0A0B] flex flex-col relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-20 left-10 w-96 h-96 rounded-full mix-blend-screen filter blur-3xl opacity-20"
          style={{ background: 'radial-gradient(circle, #D4AF37 0%, transparent 70%)' }}
          animate={{
            scale: [1, 1.2, 1],
            x: [0, 50, 0],
            y: [0, 30, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute bottom-20 right-10 w-96 h-96 rounded-full mix-blend-screen filter blur-3xl opacity-15"
          style={{ background: 'radial-gradient(circle, #CD7F32 0%, transparent 70%)' }}
          animate={{
            scale: [1, 1.3, 1],
            x: [0, -50, 0],
            y: [0, -30, 0],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full mix-blend-screen filter blur-3xl opacity-10"
          style={{ background: 'radial-gradient(circle, #800020 0%, transparent 70%)' }}
          animate={{
            scale: [1, 1.1, 1],
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
        />
      </div>

      {/* Header */}
      <div className="pt-16 pb-12 px-6 text-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <motion.div 
            className="w-32 h-32 mx-auto mb-6 rounded-full flex items-center justify-center shadow-2xl relative border-2"
            style={{ 
              background: 'linear-gradient(135deg, #D4AF37 0%, #CD7F32 100%)',
              borderColor: 'rgba(212, 175, 55, 0.5)',
              boxShadow: '0 0 40px rgba(212, 175, 55, 0.3), 0 8px 32px rgba(0, 0, 0, 0.5)'
            }}
            whileHover={{ scale: 1.05, rotate: 5 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <UtensilsCrossed size={56} className="text-[#0A0A0B]" strokeWidth={2.5} />
            <motion.div
              className="absolute -top-2 -right-2"
              animate={{ rotate: 360 }}
              transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
            >
              <Sparkles size={28} className="text-[#D4AF37]" fill="#D4AF37" />
            </motion.div>
          </motion.div>
          <motion.h1 
            className="text-6xl font-bold mb-3"
            style={{
              fontFamily: "'Playfair Display', serif",
              background: 'linear-gradient(135deg, #D4AF37 0%, #F4E4C1 50%, #D4AF37 100%)',
              backgroundSize: '200% auto',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '2px',
              textShadow: '0 0 30px rgba(212, 175, 55, 0.3)'
            }}
            animate={{ backgroundPosition: ['0%', '100%', '0%'] }}
            transition={{ duration: 5, repeat: Infinity }}
          >
            Hotel Hub
          </motion.h1>
          <motion.p
            className="text-[#D4AF37] text-sm tracking-[4px] uppercase mb-2"
            style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Fine Dining Experience
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-[#F5F5DC] text-lg mb-8"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Welcome to Our Restaurant
          </motion.p>
        </motion.div>
      </div>

      {/* Login Options */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="flex-1 px-6 max-w-md mx-auto w-full relative z-10"
      >
        <div className="space-y-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold mb-2" style={{ color: '#F5F5DC' }}>
              How would you like to continue?
            </h2>
            <p className="text-[#AEAEB2]">Choose your access type</p>
          </div>

          {/* Customer Login Button */}
          <motion.button
            onClick={() => setCurrentScreen('customer-login')}
            whileHover={{ scale: 1.02, boxShadow: "0 0 30px rgba(212, 175, 55, 0.3)" }}
            whileTap={{ scale: 0.98 }}
            className="w-full p-6 rounded-xl transition-all duration-200 text-left"
            style={{
              background: 'rgba(28, 28, 30, 0.6)',
              border: '2px solid rgba(212, 175, 55, 0.3)',
              backdropFilter: 'blur(20px)'
            }}
          >
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full flex items-center justify-center"
                   style={{ background: 'linear-gradient(135deg, #D4AF37 0%, #CD7F32 100%)' }}>
                <Users size={32} className="text-[#0A0A0B]" />
              </div>
              <div>
                <h3 className="text-2xl font-semibold mb-2" style={{ color: '#F5F5DC' }}>
                  I'm a Customer
                </h3>
                <p className="text-[#AEAEB2]">Order food for dine-in or delivery</p>
              </div>
            </div>
          </motion.button>

          {/* Admin Login Button */}
          <motion.button
            onClick={() => setCurrentScreen('admin-login')}
            whileHover={{ scale: 1.02, boxShadow: "0 0 30px rgba(212, 175, 55, 0.3)" }}
            whileTap={{ scale: 0.98 }}
            className="w-full p-6 rounded-xl transition-all duration-200 text-left"
            style={{
              background: 'rgba(28, 28, 30, 0.6)',
              border: '2px solid rgba(212, 175, 55, 0.3)',
              backdropFilter: 'blur(20px)'
            }}
          >
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full flex items-center justify-center"
                   style={{ background: 'linear-gradient(135deg, #800020 0%, #B76E79 100%)' }}>
                <Shield size={32} className="text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-semibold mb-2" style={{ color: '#F5F5DC' }}>
                  I'm Staff
                </h3>
                <p className="text-[#AEAEB2]">Access admin panel and management tools</p>
              </div>
            </div>
          </motion.button>
        </div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-12 text-center"
        >
          <p className="text-[#AEAEB2] text-sm">
            Experience luxury dining with Hotel Hub
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default WelcomeScreen;